import { useController, type FieldValues } from 'react-hook-form';
import { Checkbox, Form, type CheckboxProps } from 'semantic-ui-react';

import { FieldError } from './field-error';
import type { FieldProps } from './field.types';

export type FieldCheckboxProps<T extends FieldValues = FieldValues> =
  FieldProps<T, CheckboxProps, 'checked' | 'label'>;

/**
 * Checkbox field wired to React Hook Form via `useController`.
 *
 * Wraps SUI `Checkbox` inside a `Form.Field` and handles the SUI `onChange`
 * unwrapping (`checked` instead of `value`), error display, and
 * `aria-errormessage` wiring automatically.
 *
 * Pass `toggle` for a toggle switch variant.
 *
 * All additional props are forwarded to the underlying SUI `Checkbox`.
 */
export const FieldCheckbox = <T extends FieldValues>({
  className,
  control,
  label,
  name,
  required,
  width,
  ...rest
}: FieldCheckboxProps<T>) => {
  const { field, fieldState } = useController({ control, name });
  const id = rest.id ?? name;
  const errorId = `${id}-error`;
  const hasError = !!fieldState.error;

  return (
    <Form.Field
      className={className}
      error={hasError}
      required={required}
      width={width}
    >
      <Checkbox
        {...rest}
        ref={field.ref}
        aria-errormessage={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        checked={!!field.value}
        label={label}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(_, { checked }) => field.onChange(checked)}
      />

      <FieldError id={errorId} message={fieldState.error?.message} />
    </Form.Field>
  );
};
